import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ToastAction } from "@/components/ui/toast";
import { 
  MapPin, Calendar, Package, Send, Loader2, Check, ArrowRight, CheckCircle, Clock
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import type { Load } from "@shared/schema";

interface ShareLoadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  carrierId: string;
  carrierName: string;
  currentUserId: string;
}

interface LoadWithItems extends Load {
  items?: Array<{
    id: string;
    handlingUnit: string;
    quantity: number;
    weight: string;
  }>;
}

const ALLOW_RESHARE_PHASES = ['quote_declined', 'auto_closed'];
const ACTIVE_QUOTE_PHASES = ['quote_submitted', 'active_shipment'];

export default function ShareLoadDialog({
  open,
  onOpenChange,
  carrierId,
  carrierName,
  currentUserId,
}: ShareLoadDialogProps) {
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const [sentLoads, setSentLoads] = useState<Set<string>>(new Set());

  const { data: loads, isLoading: loadsLoading } = useQuery<LoadWithItems[]>({
    queryKey: ["/api/loads/user", currentUserId],
    queryFn: async () => {
      const response = await fetch(`/api/loads/user/${currentUserId}`);
      if (!response.ok) throw new Error("Failed to fetch loads");
      return response.json();
    },
    enabled: open,
  });

  const { data: threadStatuses = {}, isLoading: statusesLoading } = useQuery<Record<string, string>>({
    queryKey: ['/api/messages/thread-statuses', carrierId],
    enabled: open && !!carrierId,
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const shareLoadMutation = useMutation({
    mutationFn: async (loadId: string) => {
      return apiRequest("POST", "/api/messages/share-load", {
        toUserId: carrierId,
        loadId,
      });
    },
    onSuccess: (_, loadId) => {
      setSentLoads(prev => new Set(prev).add(loadId));
      queryClient.invalidateQueries({ queryKey: ["/api/messages"] });
      queryClient.invalidateQueries({ queryKey: ['/api/messages/thread-statuses', carrierId] });
      toast({
        title: "Message Thread Started",
        description: `Load details have been shared with ${carrierName}. They can now review the load and submit a quote.`,
        action: (
          <ToastAction 
            altText="View Messages" 
            onClick={() => {
              onOpenChange(false);
              setLocation(`/shipments?user=${carrierId}`);
            }}
          >
            View Messages
          </ToastAction>
        ),
      });
    },
    onError: (error: any) => {
      const message = error.message || "Could not share load details";
      const isDuplicate = message.includes("already shared");
      toast({
        title: isDuplicate ? "Already Shared" : "Failed to Share",
        description: isDuplicate
          ? `You have already shared this load with ${carrierName}.`
          : message,
        variant: "destructive",
      });
    },
  });

  const availableLoads = loads?.filter(load => load.status === "Available") || [];
  const isLoading = loadsLoading || statusesLoading;

  const formatLocation = (load: LoadWithItems) => {
    const origin = (load as any).originCity && (load as any).originState 
      ? `${(load as any).originCity}, ${(load as any).originState}`
      : load.origin;
    const dest = (load as any).destinationCity && (load as any).destinationState 
      ? `${(load as any).destinationCity}, ${(load as any).destinationState}`
      : load.destination;
    return { origin, dest };
  };

  const getLoadState = (loadId: string) => {
    if (sentLoads.has(loadId)) return 'just_sent';
    const phase = threadStatuses[loadId];
    if (!phase || ALLOW_RESHARE_PHASES.includes(phase)) return 'available';
    if (ACTIVE_QUOTE_PHASES.includes(phase)) return 'quote_in_progress';
    return 'already_shared';
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[80vh] flex flex-col overflow-hidden p-0 gap-0">

        {/* Fixed header */}
        <div className="shrink-0 px-6 pt-6 pb-4">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" data-testid="dialog-title-share-load">
              <Package className="w-5 h-5" />
              Share Load Details
            </DialogTitle>
            <DialogDescription>
              Select a load to share with {carrierName}. They'll receive the details and can submit a quote.
            </DialogDescription>
          </DialogHeader>
        </div>

        {/* Scrollable load list */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : availableLoads.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No available loads to share</p>
              <p className="text-sm mt-1">Post a load first to share it with carriers</p>
            </div>
          ) : (
            <div className="space-y-3">
              {availableLoads.map((load) => {
                const { origin, dest } = formatLocation(load);
                const loadState = getLoadState(load.id);
                const isSending = shareLoadMutation.isPending && 
                  shareLoadMutation.variables === load.id;

                return (
                  <div
                    key={load.id}
                    className="p-4 border rounded-lg hover-elevate"
                    data-testid={`load-item-${load.id}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant="secondary" className="text-xs">
                            {load.loadIdentifier || "Load"}
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            {load.weight.toLocaleString()} lbs
                          </span>
                        </div>
                        
                        <div className="flex items-center gap-2 text-sm min-w-0">
                          <MapPin className="w-3.5 h-3.5 text-green-600 shrink-0" />
                          <span className="truncate min-w-0">{origin}</span>
                          <ArrowRight className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                          <MapPin className="w-3.5 h-3.5 text-red-600 shrink-0" />
                          <span className="truncate min-w-0">{dest}</span>
                        </div>

                        <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            <span>
                              {load.pickupDate 
                                ? new Date(load.pickupDate).toLocaleDateString() 
                                : "Flexible"}
                            </span>
                          </div>
                        </div>
                      </div>

                      {loadState === 'quote_in_progress' ? (
                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground shrink-0" data-testid={`badge-quote-in-progress-${load.id}`}>
                          <Clock className="w-3.5 h-3.5" />
                          <span>Quote in progress</span>
                        </div>
                      ) : loadState === 'already_shared' ? (
                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground shrink-0" data-testid={`badge-already-shared-${load.id}`}>
                          <CheckCircle className="w-3.5 h-3.5" />
                          <span>Already shared</span>
                        </div>
                      ) : (
                        <Button
                          size="sm"
                          disabled={loadState === 'just_sent' || isSending}
                          onClick={() => shareLoadMutation.mutate(load.id)}
                          data-testid={`button-send-load-${load.id}`}
                        >
                          {isSending ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : loadState === 'just_sent' ? (
                            <>
                              <Check className="w-4 h-4 mr-1" />
                              Sent
                            </>
                          ) : (
                            <>
                              <Send className="w-4 h-4 mr-1" />
                              Send
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

      </DialogContent>
    </Dialog>
  );
}
